import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { focusManager, useQueries } from 'react-query';
import { icons, PadContainer } from 'smartsuite-ui';
import SsiFooter from '../customizations/smartSort/SsiFooter';
import { useLabelContext } from '../shared/hooks/labelContext';
import DashboardContext from '../shared/hooks/dashboardContext';
import {
    customDashboardModes,
    defaultGridRows,
    minWindowWidths,
    queryKeys,
    responsiveLayouts,
} from '../shared/constants/constants';
import SsiHeader from '../customizations/smartSort/SsiHeader';
import {
    getGridStyles,
    getGridTemplateStyles,
    getResponsiveLayoutMode,
} from '../viewDashboards/dashboardUtils';
import useWindowSize from '../shared/hooks/windowSizeHook';
import CustomDashboardWidget from './CustomDashboardWidget';
import {
    buildWidgetMetricRequests,
    getTimeZoneFilterProperties,
} from '../viewDashboards/utils/metricRequestUtils';
import { getMetricData } from '../viewDashboards/utils/fetchUtils';
import CustomDashboardHeader from './CustomDashboardHeader';
import { buildCustomDashboardRollupText } from '../shared/filtering/rollupTextUtils';
import CustomDashboardCriteria from './CustomDashboardCriteria';
import useBeforeUnload from '../shared/hooks/useBeforeUnload';

// eslint-disable-next-line complexity
export default function CustomDashboard(props) {
    const { dashboardLabels } = useLabelContext();
    const { uriConstants } = useContext(DashboardContext);

    const [isCriteriaCollapsed, setIsCriteriaCollapsed] = useState(true);

    useBeforeUnload(props.hasPendingChanges, props.isCreateMode);

    useEffect(() => {
        focusManager.setEventListener((handleFocus) => {
            const setBlur = () => handleFocus(false);
            const setFocus = () => handleFocus(true);
            if (typeof window !== 'undefined' && window.addEventListener) {
                window.addEventListener('blur', setBlur);
                window.addEventListener('focus', setFocus);
            }
            return () => {
                window.removeEventListener('blur', setBlur);
                window.removeEventListener('focus', setFocus);
            };
        });
    }, []);

    const { width } = useWindowSize();

    const [selectedResponsiveLayout, setSelectedResponsiveLayout] = useState(
        responsiveLayouts.auto
    );

    const rollupText = buildCustomDashboardRollupText(
        props.configuration.timeAndPrecision,
        dashboardLabels
    );

    const metricData = useQueries(
        // eslint-disable-next-line complexity
        props.configuration.widgets.map((widget) => {
            const timeAndPrecision = widget.useDashboardTimeRange
                ? props.configuration.timeAndPrecision
                : undefined;

            const metricRequests = buildWidgetMetricRequests(
                { ...widget, timeAndPrecision },
                widget.timeAndPrecision,
                widget.filterValues,
                widget.whereClauses,
                widget.recordLimit
            );

            const requestObject = {
                dashboardKey: widget.dashboardKey,
                viewKey: widget.viewKey,
                metricRequests,
                ...getTimeZoneFilterProperties(
                    widget.timeAndPrecision.timeZoneFiltering,
                    widget.filterValues
                ),
            };

            return {
                queryKey: [
                    queryKeys.getMetricData,
                    widget.id,
                    props.configuration,
                ],
                queryFn: () =>
                    getMetricData(
                        uriConstants.metricDataUri,
                        requestObject,
                        (error) => {
                            throw error;
                        }
                    ),
                refetchOnWindowFocus: true,
                refetchInterval: widget.refreshRateMilliseconds || false,
                staleTime: widget.refreshRateMilliseconds,
                useErrorBoundary: true,
                retry: 25,
            };
        })
    );

    const showSsiWarning = props.configuration.widgets.some(
        (widget) => widget.showSsiWarning
    );

    // eslint-disable-next-line complexity
    function getPageActions() {
        const pageActions = [];

        pageActions.push({
            description: dashboardLabels.refresh,
            iconClass: icons.refresh,
            sortOrder: 1,
            onClick: () => {
                metricData.forEach(({ refetch }) => refetch());
            },
        });

        pageActions.push({
            description: dashboardLabels.changeResponsiveLayout,
            iconClass: icons.mosaic,
            sortOrder: 2,
            onClick: () => {},
            subActions: Object.values(responsiveLayouts).map(
                (responsiveLayout, index) => ({
                    description: dashboardLabels[responsiveLayout],
                    iconClass:
                        responsiveLayout === selectedResponsiveLayout
                            ? icons.check
                            : 'menu-item-icon',
                    sortOrder: index,
                    onClick: () => {
                        setSelectedResponsiveLayout(responsiveLayout);
                    },
                })
            ),
        });

        return pageActions;
    }

    // eslint-disable-next-line complexity
    function addDesktopModifierClass(baseClass) {
        if (
            selectedResponsiveLayout === responsiveLayouts.desktop ||
            (selectedResponsiveLayout === responsiveLayouts.auto &&
                width >= minWindowWidths.desktop)
        ) {
            return `${baseClass} ${baseClass}--desktop`;
        }

        return baseClass;
    }

    // eslint-disable-next-line complexity
    function getPanelClassName(panelWidth) {
        let panelClassName = 'custom-dashboard-panel';

        const layoutMode = getResponsiveLayoutMode(
            selectedResponsiveLayout,
            width
        );

        switch (layoutMode) {
            case responsiveLayouts.mobile: {
                panelClassName += ' custom-dashboard-panel--mobile';
                return panelClassName;
            }
            case responsiveLayouts.tablet: {
                panelClassName += ' custom-dashboard-panel--tablet';

                if (panelWidth > 6) {
                    panelClassName += ' custom-dashboard-panel--full-width';
                }
                return panelClassName;
            }
            default: {
                panelClassName += ' custom-dashboard-panel--desktop';
                return panelClassName;
            }
        }
    }

    // eslint-disable-next-line complexity
    function renderWidgets() {
        return props.configuration.widgets.map((widget, index) => (
            <div
                key={widget.id}
                className={getPanelClassName(widget.position.columnSpan)}
                style={getGridStyles(widget.position)}
            >
                <CustomDashboardWidget
                    defaultFilterValues={widget.filterValues}
                    defaultTimeAndPrecision={widget.timeAndPrecision}
                    defaultWhereClauses={widget.whereClauses}
                    defaultRecordLimit={{
                        recordLimit: widget.recordLimit,
                        isDescending: !!widget.isDescending,
                    }}
                    friendlyColumnLabels={widget.friendlyColumnLabels}
                    isLoading={metricData[index].isLoading}
                    metrics={metricData[index].data || []}
                    navigateToEditor={props.navigateToEditor}
                    openLinksInNewTab={widget.openLinksInNewTab}
                    thresholdValues={props.thresholdValues}
                    widgetConfiguration={widget}
                />
            </div>
        ));
    }

    const maxGridRow = Math.max(
        ...props.configuration.widgets.map(
            (x) => x.position.row + x.position.rowSpan - 1
        ),
        defaultGridRows
    );

    return (
        <div className="dashboard">
            <CustomDashboardHeader
                isMetricDataLoading={metricData.some((x) => x.isLoading)}
                isModified={props.hasPendingChanges}
                mode={customDashboardModes.view}
                onlineHelpId={props.configuration.onlineHelpId}
                pageActions={getPageActions()}
                toggleEdit={props.navigateToEditor}
                title={props.configuration.title}
            />
            {showSsiWarning && <SsiHeader />}
            <PadContainer cssClassModifiers={['pad-medium']} />
            <CustomDashboardCriteria
                dashboardName={props.configuration.title}
                dashboardTimeAndPrecision={props.configuration.timeAndPrecision}
                isCollapsed={isCriteriaCollapsed}
                isReadOnly={true}
                onCollapse={() => setIsCriteriaCollapsed(!isCriteriaCollapsed)}
                rollupText={rollupText}
            />
            <PadContainer cssClassModifiers={['pad-small']} />
            <div className={addDesktopModifierClass('dashboard-widgets')}>
                <div
                    className={addDesktopModifierClass('dashboard-widget-grid')}
                    style={getGridTemplateStyles(maxGridRow)}
                >
                    {renderWidgets()}
                </div>
            </div>
            {showSsiWarning && <SsiFooter />}
        </div>
    );
}

CustomDashboard.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    hasPendingChanges: PropTypes.bool,
    isCreateMode: PropTypes.bool,
    navigateToEditor: PropTypes.func.isRequired,
    thresholdValues: PropTypes.instanceOf(Array),
};

CustomDashboard.defaultProps = {
    hasPendingChanges: false,
    isCreateMode: false,
};
